/**
 * A mixed SERP is only a decision when your page's own type is not in the mix.
 *
 * "Mixed" means Google is ranking several shapes for one query — a definition,
 * a how-to and a template all on page one. The card treated that as a choice to
 * make, and told the operator to "pick one page type to align with" even when
 * their page was ALREADY one of the shapes ranking. On
 * `/templates/project-scope-statement` the SERP mix is definition / how-to /
 * template, the page is a template, and smartsheet.com ranks #5 with the same
 * type. There is nothing to pick: the question the card asks was answered
 * before it was asked.
 *
 * The mix still matters when your type is absent — then you are the odd shape
 * out and choosing which winner to look like is real work. So the check is
 * membership, not consensus.
 */

import type { SerpRivalVoteRow, SerpRowAnswerKind } from './api-types';

export interface PageTypeRow {
  position: number;
  family: string;
  domain?: string;
}

export interface MixedSerpAlignment {
  /** Your page's type, as it ranks somewhere on this SERP. */
  family: string;
  /** The best-placed rival that shares it, when we know who. */
  position: number | null;
  domain: string | null;
}

const UNKNOWN = new Set(['', 'unknown', 'unclear', 'mixed']);

/**
 * Null unless the SERP is mixed AND your type is one of the shapes on it.
 *
 * `rows` are the SERP's own results; `samples` carry the domain names where the
 * diagnosis captured them, so the answer can name who rather than just assert
 * it. Rows without a usable family are ignored rather than treated as a match —
 * "unknown" is the absence of a classification, not a type your page shares.
 */
export function mixedSerpAlignment(
  serpFamily: string | null | undefined,
  yourFamily: string | null | undefined,
  rows: readonly PageTypeRow[] | null | undefined,
  samples?: readonly PageTypeRow[] | null
): MixedSerpAlignment | null {
  if ((serpFamily || '').trim() !== 'mixed') return null;

  const yours = (yourFamily || '').trim().toLowerCase();
  if (!yours || UNKNOWN.has(yours)) return null;

  const matches = [...(rows || []), ...(samples || [])]
    .filter((row) => (row?.family || '').trim().toLowerCase() === yours)
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  if (!matches.length) return null;

  // The best-placed match names it, and a sample is preferred at equal position
  // because only samples carry a domain.
  const named = matches.find((row) => (row.domain || '').trim());
  const best = named && named.position === matches[0].position ? named : matches[0];

  return {
    family: yours,
    position: best.position || null,
    domain: (best.domain || '').trim() || null
  };
}

/** One SERP result offered as an example of the shape Google is rewarding. */
export interface PageTypeExample {
  position: number;
  domain: string;
  title: string;
}

export interface PageTypeExampleRow extends PageTypeRow {
  title?: string;
}

const MAX_EXAMPLES = 2;

/**
 * The best-placed results that ARE the type the SERP expects.
 *
 * The card asserts "SERP expects definition" and, until this, gave nothing to
 * check it against: the operator either trusted the verdict or opened Google
 * themselves, which is the clicking-around the review belt exists to remove.
 * Two titles make the claim falsifiable at a glance, and they double as the
 * brief — this is the shape to write.
 *
 * Two, not five. The card is one of eight on a belt page and the third example
 * adds confidence to nobody who was not already convinced by the first.
 *
 * Deliberately silent when:
 * - the SERP is `mixed` — that case has its own table, which shows the spread
 *   rather than a consensus, and two "examples" of a consensus that does not
 *   exist would be a lie;
 * - the family is unknown — there is no claim to support;
 * - a result is your own page — telling someone to look at the page they are
 *   deciding about is noise, whatever its type.
 */
export function serpPageTypeExamples(
  serpFamily: string | null | undefined,
  rows: readonly PageTypeExampleRow[] | null | undefined,
  yourUrlOrDomain?: string | null
): PageTypeExample[] {
  const family = (serpFamily || '').trim().toLowerCase();
  if (!family || UNKNOWN.has(family)) return [];

  const yours = (yourUrlOrDomain || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0];

  return [...(rows || [])]
    .filter((row) => (row?.family || '').trim().toLowerCase() === family)
    .filter((row) => {
      const domain = (row.domain || '').trim().toLowerCase().replace(/^www\./, '');
      const title = (row.title || '').trim();
      if (!domain || !title) return false;
      return !yours || domain !== yours;
    })
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .slice(0, MAX_EXAMPLES)
    .map((row) => ({
      position: row.position || 0,
      domain: (row.domain || '').trim().replace(/^www\./, ''),
      title: (row.title || '').trim()
    }));
}

/**
 * At or above this position in the stored results for the search a page was
 * compared on, the review surface gives no page-type advice: Google already
 * ranks the page's own format for that search (owner decision, quality sprint 4
 * Q2).
 *
 * The data, from the two graded walks of 2026-10-18 (every page-type item on
 * both review queues, 13 in all). SlideSpeak: 3 graded wrong for this reason, on
 * tool pages at #1, #2 and #2 ("its format is already winning"); one at #5 was
 * graded unfinished, not wrong; the other 4 are not in the stored results.
 * ManyRequests: the only page-type item whose page is in its results is at #7.
 * No page-type item on either site sits at #3 or #4, so the cut-off could move
 * one place either way without changing what these walks show; 3 is the owner's
 * number.
 */
export const PAGE_TYPE_ADVICE_MAX_OWN_RANK = 3;

/** True when the page's own rank says its type is already what Google rewards for the search. */
export function pageTypeAdviceWithheldByRank(yourPosition: number | null | undefined): boolean {
  return (
    typeof yourPosition === 'number' &&
    yourPosition >= 1 &&
    yourPosition <= PAGE_TYPE_ADVICE_MAX_OWN_RANK
  );
}

/**
 * The first change that turns a page into the type the results expect, or null
 * when we cannot name one we know the page lacks.
 *
 * A verdict alone ("SERP expects how-to; your page reads tool") leaves the writer
 * to work out what to do, and the graders marked every one unfinished or wrong.
 * So it ships with a change or not at all, and a change is named only where the
 * diagnosis proves the page lacks it.
 *
 * Only `template` qualifies today. A main-content link to the file votes
 * `template` at path weight in the page-type read, and evidence at that weight
 * that agrees with the results makes the page read as the results' type
 * (`resolvePageTypeFamily`, backend). So a page that reads as anything else on a
 * template search has no file to hand over, and that is the first change. That
 * shape was graded good on every walk that had it (sprints 1 and 2: a templates
 * article with download copy and no file).
 *
 * Every other type has no such proof. "Add numbered steps" would be told to a
 * page with eight numbered strategies, and "open with a definition" to one with a
 * "What is …" heading, which are the wrong items on the 2026-10-18 walks. A tool
 * page gets no first change here: a change to its whole type is
 * `pageTypeWholePageVerdict`'s, which needs the rivals to agree, and the steps the
 * results give are the answer-structure card's to name.
 */
export function pageTypeFirstChange(
  serpFamily: string | null | undefined,
  yourFamily: string | null | undefined
): string | null {
  const serp = (serpFamily || '').trim().toLowerCase();
  const yours = (yourFamily || '').trim().toLowerCase();
  if (!serp || UNKNOWN.has(serp) || serp === yours) return null;
  if (yours === 'online_tool') return null;
  if (serp === 'template') {
    return 'First change: hand over the template itself, as a Google Docs, Sheets or Slides link or a .docx, .xlsx or .pptx download, near the top of the page.';
  }
  return null;
}

/**
 * The verdict on the whole page's type needs this share of the rivals counted
 * (the first seven results that are not the page's own site) to be of the type
 * the results expect.
 *
 * The data, both sites on the frozen sprint 5 base (2026-10-19), every page whose
 * results expect a how-to and whose page reads as a tool (the only pair with a
 * whole-page change today). SlideSpeak, 39 pages: 1 of 7 (4 pages), 2 of 7 (4),
 * 3 of 7 (2), 4 of 7 (1), 4 of 5 (1), 5 of 7 (3), 6 of 6 (1), 6 of 7 (7), 7 of 7
 * (16). Nothing sits between 4 of 7 (0.57) and 5 of 7 (0.71), so 0.7 takes five
 * of seven and leaves the 4-of-7 page and everything below; anywhere from 0.58
 * to 0.71 draws the same line. Below it most rivals are not classified at all (a
 * help page titled with the task, not "how to"), and "1 of the 7 other results
 * is a how-to" is not a verdict. ManyRequests: no page has this pair, so it adds
 * no data and nothing there moves.
 *
 * Since quality sprint 6 G the how-to count is of results that give instructions, kind by kind
 * (`instructionVote`), not of the `how_to` family. Same pages, frozen sprint 6 base: 1 of 7 (1
 * page), 2 of 7 (3), 3 of 7 (4), 4 of 7 (4), 5 of 7 (6), 5 of 5 (1), 6 of 6 (1), 6 of 7 (20), 7 of 7
 * (9). Still nothing between 4 of 7 and 5 of 7, so the share is unchanged.
 */
export const PAGE_TYPE_VERDICT_MIN_RIVAL_SHARE = 0.7;

/**
 * And at least this many rivals counted, so a results page with two rows cannot
 * make "2 of the 2" a consensus. The smallest count among the qualifying pages
 * above is 5 (4 of 5, the only one under 6).
 */
export const PAGE_TYPE_VERDICT_MIN_RIVALS = 5;

/** What a page of each type is called in a sentence, one and many. */
const PAGE_TYPE_NOUN: Record<string, { one: string; many: string }> = {
  how_to: { one: 'how-to guide', many: 'how-to guides' },
  online_tool: { one: 'tool', many: 'tools' },
  template: { one: 'template', many: 'templates' },
  definition: { one: 'definition', many: 'definitions' },
  listicle: { one: 'list roundup', many: 'list roundups' },
  versus: { one: 'comparison', many: 'comparisons' },
  pricing: { one: 'pricing page', many: 'pricing pages' }
};

/**
 * The kinds of result that answer a how-to search with instructions, in the order the count names
 * them, and what each is called (quality sprint 6 G). The count used to call every one of them a
 * "how-to guide", and sprint 5's grader found Q&A threads and code tutorials among the guides; the
 * kinds are read per result server-side (`serp-row-answer-kind.ts`) and the count names each.
 */
const INSTRUCTION_KIND_NOUN: ReadonlyArray<readonly [SerpRowAnswerKind, { one: string; many: string }]> = [
  ['how_to_guide', { one: 'how-to guide', many: 'how-to guides' }],
  ['how_to_video', { one: 'how-to video', many: 'how-to videos' }],
  ['qa_thread', { one: 'Q&A thread', many: 'Q&A threads' }],
  ['code_tutorial', { one: 'code tutorial', many: 'code tutorials' }]
];

const INSTRUCTION_KINDS: ReadonlySet<SerpRowAnswerKind> = new Set(INSTRUCTION_KIND_NOUN.map(([kind]) => kind));

/** The app's own route, when the results agree on it (`answerStructureInAppRoute`). */
export interface PageTypeVerdictRoute {
  entry: string;
  steps: string[];
}

function routeText(route: PageTypeVerdictRoute): string {
  return [route.entry, ...route.steps].join(' › ');
}

/**
 * The page-level change for a pair (what the results expect > what the page is).
 * Written per pair of types, never per site: what a page of the one type becomes
 * to be the other, keeping what it has.
 *
 * Only a tool on how-to results today, and the change turns on whether the offer
 * is on the page (`pageOfferGated`, read from the stored HTML). A working tool is
 * what the page earns its traffic with, so it stays first and the steps go below
 * it; putting the steps first risks the page's own traffic (graded wrong on the
 * sprint 5 checkpoint, a working upload with traffic). A tool behind an email
 * form gives searchers nothing to use, so there the guide comes first, and the tool
 * is not offered as "the quick way" to do the job: it does not work yet (sprint 5's
 * grader: that overpromises). When the results agree on the app's own route, the
 * change names it: the steps item that used to say so separately repeated the verdict
 * (sprint 5's grader, on three pages). Other pairs have no change we can name without
 * knowing what the page lacks (see `pageTypeFirstChange`).
 */
const PAGE_TYPE_WHOLE_PAGE_CHANGE: Record<
  string,
  { offerOnPage: (route: PageTypeVerdictRoute | null) => string; offerGated: (route: PageTypeVerdictRoute | null) => string }
> = {
  'how_to>online_tool': {
    offerOnPage: (route) =>
      route
        ? `keep the tool first and add the app’s own steps (${routeText(route)}) as a section below it.`
        : 'keep the tool first and add the how-to steps the results give as a section below it.',
    offerGated: (route) =>
      route
        ? `make this page a how-to guide with the tool inside it, the app’s own steps first (${routeText(route)}).`
        : 'make this page a how-to guide with the tool inside it, the steps first.'
  }
};

/** The fact stated when the page's offer is behind an email form. */
const GATED_OFFER_FACT: Record<'tool' | 'template', string> = {
  tool: 'The tool itself is behind an email form: the page asks for an email address and has no upload or entry field.',
  template: 'The template itself is behind an email form: the page asks for an email address and links no file.'
};

export interface PageTypeWholePageInput {
  serpFamily: string | null | undefined;
  yourFamily: string | null | undefined;
  vote:
    | {
        count: number;
        total: number;
        ownPositions: number[];
        sameTypeAsPage: number;
        rows?: ReadonlyArray<SerpRivalVoteRow> | null;
      }
    | null
    | undefined;
  /** The page's own position for the search, only when the result is this URL. */
  ownPosition: number | null | undefined;
  gated: 'tool' | 'template' | null | undefined;
  /** The app's own route the results agree on, folded into the change. */
  route?: PageTypeVerdictRoute | null;
}

export interface PageTypeWholePageVerdict {
  /** The verdict and the change: what the card asks for. */
  advice: string;
  /** The count behind it, and the gated-offer fact when there is one. */
  evidence: string;
  /**
   * The counted rivals that give instructions for the page's own job, best placed first: the
   * results to show as examples of the verdict. Empty for a payload without per-row kinds.
   */
  examplePositions: number[];
}

/** "3 how-to guides, 2 how-to videos and 1 Q&A thread". */
function instructionBreakdown(rows: ReadonlyArray<SerpRivalVoteRow>): string {
  const parts = INSTRUCTION_KIND_NOUN.map(([kind, noun]) => {
    const n = rows.filter((r) => r.kind === kind).length;
    return n ? `${n} ${n === 1 ? noun.one : noun.many}` : null;
  }).filter((p): p is string => !!p);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] || '';
}

/**
 * The how-to vote, kind by kind (quality sprint 6 G), or null when the payload has no per-row
 * kinds (it predates them) and the family count stands.
 *
 * Two conditions, at the same share:
 * - `PAGE_TYPE_VERDICT_MIN_RIVAL_SHARE` of the counted rivals give instructions (a how-to guide or
 *   video, a Q&A thread, a code tutorial): what Google ranks for the search;
 * - and so do that share of the counted rivals left once those asking for the opposite of the
 *   page's job are set aside (sprint 3's task test, as the app's-own-route reader applies it per
 *   result, sprint 5 B). A de-duplicating tool whose results are how-tos for duplicating a slide
 *   was told "Google wants a how-to here" (graded wrong, wrong keyword): its how-tos teach the
 *   opposite job. Measured on the frozen sprint 6 base, every tool page on how-to results
 *   (SlideSpeak 49, ManyRequests none): 12 have a rival for the opposite job, 10 of them pass the
 *   first condition. Of those 10 the second takes out only the de-duplicator: 6 of its 7 rivals
 *   are for the opposite job and the one left is not instructions (0 of 1). The read-only
 *   remover, graded good, has 4 of 7 for the opposite job (making a file read-only) and its 3
 *   on-task rivals all give instructions (3 of 3); the other 8 are at 3 of 4 (two pages, the
 *   closest to the line: one more on-task rival that is not instructions and they would go) or
 *   5 of 6 and above. The opposite-job rivals still count in the first condition: the results'
 *   format is what it is. The task test reads words, so it marks a few rivals wrongly (a guide
 *   whose title says "put" on a converter page reads as inserting): that is why the count never states it.
 */
function instructionVote(
  vote: NonNullable<PageTypeWholePageInput['vote']>
): { holds: boolean; instructions: ReadonlyArray<SerpRivalVoteRow>; onTask: ReadonlyArray<SerpRivalVoteRow> } | null {
  const rows = vote.rows;
  if (!rows?.length) return null;
  const instructions = rows.filter((r) => INSTRUCTION_KINDS.has(r.kind));
  const onTaskRows = rows.filter((r) => !r.againstPageTask);
  const onTask = onTaskRows.filter((r) => INSTRUCTION_KINDS.has(r.kind));
  const holds =
    rows.length >= PAGE_TYPE_VERDICT_MIN_RIVALS &&
    instructions.length / rows.length >= PAGE_TYPE_VERDICT_MIN_RIVAL_SHARE &&
    onTaskRows.length > 0 &&
    onTask.length / onTaskRows.length >= PAGE_TYPE_VERDICT_MIN_RIVAL_SHARE;
  return { holds, instructions, onTask };
}

/**
 * A verdict on the whole page's type ("SERP expects how-to answers here, not
 * tools") with the page-level change that follows, or null.
 *
 * Shown whatever the page's own rank (owner, §0an: "not simply dismiss because
 * we are already at #1, but they need to make sense then"). On a page in the top
 * `PAGE_TYPE_ADVICE_MAX_OWN_RANK` the change is framed as keeping the position,
 * and the count says the page is the odd one out. Null unless the pair has a
 * change and the rivals agree; our own result never votes.
 */
export function pageTypeWholePageVerdict(input: PageTypeWholePageInput): PageTypeWholePageVerdict | null {
  const serp = (input.serpFamily || '').trim().toLowerCase();
  const yours = (input.yourFamily || '').trim().toLowerCase();
  const changes = PAGE_TYPE_WHOLE_PAGE_CHANGE[`${serp}>${yours}`];
  const vote = input.vote;
  if (!changes || !vote) return null;
  const route = input.route && input.route.steps.length ? input.route : null;
  const change = input.gated ? changes.offerGated(route) : changes.offerOnPage(route);

  const byKind = serp === 'how_to' ? instructionVote(vote) : null;
  if (byKind) {
    if (!byKind.holds) return null;
  } else {
    if (vote.total < PAGE_TYPE_VERDICT_MIN_RIVALS) return null;
    if (vote.count / vote.total < PAGE_TYPE_VERDICT_MIN_RIVAL_SHARE) return null;
  }

  const serpNoun = byKind ? { one: 'how-to answer', many: 'how-to answers' } : PAGE_TYPE_NOUN[serp];
  const yourNoun = PAGE_TYPE_NOUN[yours];
  const position =
    typeof input.ownPosition === 'number' && input.ownPosition >= 1 ? input.ownPosition : null;

  // Says what was counted: the top `total` rivals, not every stored result (often 8 or 9).
  const count = byKind ? byKind.instructions.length : vote.count;
  const total = byKind ? vote.rows!.length : vote.total;
  const share = count === total ? `All ${count}` : `${count}`;
  const counted =
    `${share} of the top ${total} ${vote.ownPositions.length ? 'other ' : ''}results are ${serpNoun.many}` +
    (byKind ? `: ${instructionBreakdown(byKind.instructions)}` : '');
  const sameType = byKind ? vote.rows!.filter((r) => r.kind === 'tool').length : vote.sameTypeAsPage;
  let yoursPart: string;
  if (position && sameType === 0) {
    yoursPart = `yours is the only ${yourNoun.one}, at #${position}.`;
  } else if (position) {
    yoursPart = `yours is a ${yourNoun.one}, at #${position}.`;
  } else {
    yoursPart = `this page is a ${yourNoun.one}.`;
  }
  const gatedFact = input.gated ? ` ${GATED_OFFER_FACT[input.gated]}` : '';

  const defending = position !== null && position <= PAGE_TYPE_ADVICE_MAX_OWN_RANK;
  const doThis = defending
    ? `To keep #${position}, ${change}`
    : `${change.charAt(0).toUpperCase()}${change.slice(1)}`;

  // Guides and videos before threads: an example is the shape to write.
  const examplePositions = byKind
    ? [...byKind.onTask]
        .sort(
          (a, b) =>
            Number(a.kind === 'qa_thread' || a.kind === 'code_tutorial') -
              Number(b.kind === 'qa_thread' || b.kind === 'code_tutorial') || a.position - b.position
        )
        .map((r) => r.position)
    : [];

  return {
    // "SERP expects" opens every page-type item, which is how the graded walks file it.
    advice: `SERP expects ${serpNoun.many} here, not ${yourNoun.many}. ${doThis}`,
    evidence: `${counted}; ${yoursPart}${gatedFact}`,
    examplePositions
  };
}
